import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Camera, BookOpen, Sparkles, ArrowRight, Smile, Meh, Frown, Share2 } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

const BACKEND_ROOT = API.replace(/\/api$/, "");

const STEMMING_ICON = { blij: Smile, neutraal: Meh, verdrietig: Frown, boos: Frown, moe: Meh };

const relDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso.length <= 10 ? iso + "T12:00:00" : iso);
    const days = Math.round((new Date() - d) / 86_400_000);
    if (days <= 0) return "vandaag";
    if (days === 1) return "gisteren";
    if (days < 7) return `${days} dagen geleden`;
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  } catch { return ""; }
};

/** Rendert een hoogtepunt-quote als 1080x1080 PNG-blob voor delen.
 *  Warm sfeerkleur, geen kind-foto (privacy) — alleen tekst, naam, datum, Webster mark.
 */
async function renderQuoteCard(quote) {
  const size = 1080;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Achtergrond: warm verloop (sage → cream → terracotta accent)
  const grad = ctx.createLinearGradient(0, 0, size, size);
  grad.addColorStop(0, "#3a5a40");
  grad.addColorStop(0.55, "#6b8e5a");
  grad.addColorStop(1, "#c9a86a");
  ctx.fillStyle = grad; ctx.fillRect(0, 0, size, size);

  // Subtiele "grain" — schaduwvlekken voor warmte
  ctx.globalAlpha = 0.045;
  for (let i = 0; i < 22; i++) {
    ctx.fillStyle = i % 2 ? "#ffffff" : "#000000";
    const r = 80 + Math.random() * 160;
    ctx.beginPath();
    ctx.arc(Math.random() * size, Math.random() * size, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Sparkles-icoon boven
  ctx.font = "bold 72px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.textAlign = "center";
  ctx.fillText("✨", size / 2, 180);

  // Header label
  ctx.font = "600 26px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText("HOOGTEPUNT VAN DE WEEK", size / 2, 240);

  // Quote — wrap in max 8 regels op 40 char breed
  const text = (quote.tekst || "").trim();
  ctx.font = "italic 500 52px 'Georgia', 'Times New Roman', serif";
  ctx.fillStyle = "#ffffff";
  const maxW = size - 200;
  const words = text.split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? cur + " " + w : w;
    if (ctx.measureText(test).width > maxW && cur) {
      lines.push(cur); cur = w;
    } else { cur = test; }
    if (lines.length >= 8) break;
  }
  if (cur && lines.length < 8) lines.push(cur);

  const lineH = 74;
  const totalH = lines.length * lineH;
  const startY = (size - totalH) / 2 - 40;
  // Regels
  ctx.font = "italic 500 52px 'Georgia', 'Times New Roman', serif";
  ctx.fillStyle = "#ffffff";
  lines.forEach((ln, i) => ctx.fillText(ln, size / 2, startY + i * lineH));

  // Kind + auteur + datum
  const meta = [];
  if (quote.kind_voornaam) meta.push(quote.kind_voornaam);
  if (quote.van_naam) meta.push("door " + quote.van_naam);
  if (quote.datum) {
    try {
      const d = new Date(quote.datum + "T12:00:00");
      meta.push(d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }));
    } catch { /* noop */ }
  }
  ctx.font = "600 30px system-ui, -apple-system, 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.fillText(meta.join("  ·  "), size / 2, startY + totalH + 60);

  // Footer — Webster mark subtiel
  ctx.font = "500 22px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  ctx.fillText("Gedeeld via Webster · Kinderopvang", size / 2, size - 60);

  // Naar Blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error("canvas.toBlob mislukt")), "image/png", 0.95);
  });
}

async function shareQuote(quote) {
  try {
    const blob = await renderQuoteCard(quote);
    const file = new File([blob], `hoogtepunt-${quote.datum || "webster"}.png`, { type: "image/png" });
    const shareText = `${quote.tekst}${quote.kind_voornaam ? ` — ${quote.kind_voornaam}` : ""}`;
    // Native Web Share API met bestand (mobile Safari, Chrome Android)
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text: shareText, title: "Hoogtepunt van de week" });
      return { shared: true };
    }
    // Fallback: download PNG
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return { shared: false, downloaded: true };
  } catch (e) {
    if (e && e.name === "AbortError") return { shared: false, cancelled: true };
    throw e;
  }
}

/**
 * Ouder-portaal Highlights welkomstscherm.
 * Toont bovenaan de portal-home een warme kaart met:
 *   - Meest recente hoogtepunt-quote uit een schriftje
 *   - 4-6 recente foto's van álle eigen kinderen (7 dagen)
 *   - Nieuwste 1-3 schriftje-entries
 *   - Snelle CTA's naar Album en Dagverslag
 *
 * Props:
 *  - onOpenPhoto(photo)   → opent lightbox in parent
 *  - onNavigate(path)     → useNavigate handler
 */
export default function OuderHighlightsCard({ onOpenPhoto, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/portaal/highlights", { params: { days: 7, limit_photos: 6, limit_schriftjes: 3 } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const kidNames = useMemo(
    () => (data?.kinderen || []).map((k) => k.voornaam).filter(Boolean),
    [data]
  );

  if (loading) {
    return (
      <Card
        data-testid="ouder-highlights-loading"
        className="mb-6 p-5 bg-white shadow-none border border-neutral-200"
      >
        <div className="animate-pulse flex flex-col gap-3">
          <div className="h-4 w-1/3 bg-neutral-100 rounded" />
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-square bg-neutral-100 rounded-md" />
            ))}
          </div>
          <div className="h-16 bg-neutral-100 rounded-md" />
        </div>
      </Card>
    );
  }

  if (!data) return null;

  const hasPhotos = (data.photos || []).length > 0;
  const hasSchriftjes = (data.schriftjes || []).length > 0;

  if (!hasPhotos && !hasSchriftjes && !data.highlight_quote) {
    // Empty-state — vriendelijke boodschap ipv niets tonen
    return (
      <Card
        data-testid="ouder-highlights-empty"
        className="mb-6 p-5 bg-gradient-to-br from-[color:var(--sage-100)]/40 to-white shadow-none border border-[color:var(--sage-100)]"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-white/60">
            <Sparkles className="h-5 w-5 text-[color:var(--sage)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-neutral-800 text-sm">
              Nog geen highlights deze week
            </div>
            <div className="text-[11px] text-neutral-500">
              Zodra de leidsters foto's of schriftjes delen, verschijnen ze hier bovenaan.
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      data-testid="ouder-highlights"
      className="mb-6 overflow-hidden shadow-none border border-[color:var(--sage-100)] bg-gradient-to-br from-[color:var(--sage-100)]/50 via-white to-amber-50/40"
    >
      <div className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[color:var(--sage)] text-white shrink-0">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                Deze week
              </div>
              <div className="font-display text-base sm:text-lg tracking-tight truncate">
                Hoogtepunten
                {kidNames.length > 0 && (
                  <span className="text-neutral-500 font-sans font-normal text-sm">
                    {" — "}{kidNames.join(" & ")}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-neutral-500 shrink-0 hidden sm:block">
            Laatste {data.days} dagen
          </div>
        </div>

        {/* Quote-strook */}
        {data.highlight_quote && (
          <div
            data-testid="ouder-highlights-quote"
            className="mb-4 p-3 rounded-lg bg-white/70 border border-[color:var(--sage-100)] backdrop-blur-sm"
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--sage)]">
                Hoogtepunt
              </div>
              <ShareQuoteButton quote={data.highlight_quote} />
            </div>
            <blockquote className="text-sm text-neutral-700 italic leading-snug">
              "{data.highlight_quote.tekst}"
            </blockquote>
            <div className="text-[11px] text-neutral-500 mt-1">
              {data.highlight_quote.kind_voornaam && (
                <span className="font-medium text-neutral-700">
                  {data.highlight_quote.kind_voornaam}
                </span>
              )}
              {data.highlight_quote.van_naam && (
                <> · door {data.highlight_quote.van_naam}</>
              )}
              {data.highlight_quote.datum && (
                <> · {relDate(data.highlight_quote.datum)}</>
              )}
            </div>
          </div>
        )}

        {/* Photo grid */}
        {hasPhotos && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-500">
                <Camera className="h-3 w-3" />
                Nieuwste foto's
              </div>
              <button
                type="button"
                onClick={() => onNavigate?.("/portaal/album")}
                data-testid="ouder-highlights-album-link"
                className="text-[11px] text-[color:var(--terracotta)] hover:underline flex items-center gap-1"
              >
                Alle foto's <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {data.photos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onOpenPhoto?.(p)}
                  data-testid={`ouder-highlights-photo-${p.id}`}
                  className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 border border-neutral-200 hover:ring-2 hover:ring-[color:var(--terracotta)]/60 transition"
                  title={p.caption || `${(p.kind_namen || []).join(", ")} · ${relDate(p.created_at)}`}
                >
                  <img
                    src={`${BACKEND_ROOT}${p.url}`}
                    alt={p.caption || "foto"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-1 bg-gradient-to-t from-black/70 to-transparent text-[9px] text-white leading-tight text-left opacity-0 group-hover:opacity-100 transition">
                    {(p.kind_namen || []).join(" · ")}
                    {p.created_at && <div>{relDate(p.created_at)}</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Schriftjes */}
        {hasSchriftjes && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-neutral-500">
                <BookOpen className="h-3 w-3" />
                Nieuwste schriftjes
              </div>
              <button
                type="button"
                onClick={() => onNavigate?.("/portaal/dagverslag")}
                data-testid="ouder-highlights-dagverslag-link"
                className="text-[11px] text-[color:var(--sage)] hover:underline flex items-center gap-1"
              >
                Alles bekijken <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {data.schriftjes.map((s) => {
                const St = STEMMING_ICON[s.stemming] || Smile;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onNavigate?.("/portaal/dagverslag")}
                    data-testid={`ouder-highlights-schriftje-${s.id}`}
                    className="text-left rounded-md border border-neutral-200 bg-white p-3 hover:border-[color:var(--sage)] hover:bg-[color:var(--sage-100)]/30 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)] shrink-0">
                        <St className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold text-neutral-800 truncate">
                          {s.kind_voornaam || "Kind"}
                        </div>
                        <div className="text-[10px] text-neutral-500">
                          {relDate(s.datum)}
                          {s.stemming && <span className="capitalize"> · {s.stemming}</span>}
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-700 line-clamp-3 leading-snug">
                      {s.hoogtepunt || s.activiteiten || "Bekijk het schriftje voor details."}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer CTA — subtiel */}
        <div className="mt-4 pt-3 border-t border-neutral-100 flex items-center justify-between gap-2">
          <div className="text-[11px] text-neutral-500">
            {data.counts.photos + data.counts.schriftjes} updates in de laatste {data.days} dagen
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onNavigate?.("/portaal/album")}
            data-testid="ouder-highlights-cta"
            className="h-7 text-xs text-[color:var(--sage)] hover:bg-[color:var(--sage)]/5"
          >
            Bekijk alles <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>
    </Card>
  );
}


function ShareQuoteButton({ quote }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (busy || !quote?.tekst) return;
    setBusy(true);
    try {
      const res = await shareQuote(quote);
      if (res.shared) {
        toast.success("Kaart gedeeld — leuk voor opa en oma!");
      } else if (res.downloaded) {
        toast.success("Kaart gedownload — deel 'm via WhatsApp of e-mail");
      }
    } catch (e) {
      toast.error("Delen mislukt — probeer opnieuw");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      onClick={handle}
      disabled={busy}
      data-testid="ouder-highlights-quote-share"
      title="Deel deze hoogtepunt-quote als kaart met opa en oma"
      className="h-7 -mr-1 px-2 text-[11px] text-[color:var(--sage)] hover:bg-[color:var(--sage)]/5"
    >
      <Share2 className="h-3.5 w-3.5 mr-1" />
      {busy ? "Bezig…" : "Deel"}
    </Button>
  );
}
